import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
// =====================
// Type definitions des réponses des services
// =====================

type Declaration = {
    /**
     * Code postal rattaché au PCE
     */
    code_postal: string
    /**
     * Courriel du titulaire du PCE
     */
    courriel_titulaire: string
    /**
     * Date de création du droit d'accès
     */
    date_creation: string
    /**
     * Motif du passage à refusé du droit d'accès
     */
    date_debut_droit_acces: string
    /**
     * Date de fin du droit d'accès
     */
    date_fin_droit_acces: string
    /**
     * Date du passage à obsolète du droit d'accès
     */
    date_passage_a_obsolete: null | string
    /**
     * Date du passage à refusé du droit d'accès
     */
    date_passage_a_refuse: null | string
    /**
     * Date de révocation du droit d'accès
     */
    date_revocation: null | string
    /**
     * Etat du droit d'accès :
     * - Active : Le droit d'accès est actif, les données peuvent être consultées
     * - Obsolète : Le droit d'accès n'est plus actif, si le consentement est toujours valide,
     * un nouveau droit d'accès peut être déclaré. Si le consentement n'est plus valide, un
     * nouveau consentement doit être demandé
     * - Refusée : Le détenteur du point a refusé de donner son droit d'accès. Il faut se
     * rapprocher du détenteur du point pour comprendre les raisons et l'inviter à annuler cette
     * action dans son espace GRDF.
     * - Révopquée : Le droit d'accès a été révoqué
     */
    etat_droit_acces: string
    /**
     * Identifiant associé au droit d'accès dont dispose le tiers sur un PCE
     */
    id_droit_acces: string
    /**
     * Numéro unique identifiant le point de comptage et d'estimation
     */
    id_pce: string
    /**
     * Numéro de téléphone du tituliare renseigné pour recevoir la confirmation de droit d'accès
     * GRDF
     */
    numero_telephone_mobile_titulaire: null | string
    /**
     * Type de parcours d'accès aux données
     */
    parcours: string
    /**
     * Date à partir de laquelle les données de consommation peuvent être consultées
     */
    perim_donnees_conso_debut: string
    /**
     * Date jusqu'à laquelle les données de consommation peuvent être consultées
     */
    perim_donnees_conso_fin: string
    /**
     * Indique si le droit d'accès permet d'accéder aux données contractuelles
     */
    perim_donnees_contractuelles: boolean
    /**
     * Indique si le droit d'accès permet d'accéder aux données informatives
     */
    perim_donnees_informatives: boolean
    /**
     * Indique si le droit d'accès permet d'accéder aux données publiees
     */
    perim_donnees_publiees: boolean
    /**
     * Indique si le droit d'accès permet d'accéder aux données techniques
     */
    perim_donnees_techniques: boolean
    /**
     * Raison sociale du tiers en sa qualité de personne morale
     */
    raison_sociale_du_tiers: string
    /**
     * Raison sociale du titulaire en sa qualité de personne morale
     */
    raison_sociale_du_titulaire: string
    /**
     * Un tiers est autorisé à accéder aux données d'un PCE s'il en est son détenteur
     * (Détenteur) Un tiers est autorisé à accéder aux données d'un PCE s'il dispose du
     * consentement du titulaire (Autorisé)
     */
    role_tiers: string
    /**
     * Motif du passage à obsolète du droit d'accès
     */
    source_passage_a_obsolete: null | string
    /**
     * Motif du passage à refusé du droit d'accès
     */
    source_passage_a_refuse: null | string
    /**
     * Motif de la révocation du droit d'accès
     */
    source_revocation: null | string
}

export type GetLastDeclarationResponse = Declaration

export type CreateDeclarationResponse = Declaration

/**
 * Réponse 200 de GET /api/metier/v5/source/search/pce (source.get.search.idtype.route.js).
 * Shape exacte renvoyée par l'API : { source, consentement, hasAccess }.
 * Pour PCE la projection source inclut operation.pce.droit_acces.etat_droit_acces.
 */
export type VerifyPceApiResponse = {
    /** Source trouvée (projection PCE: _id, idtype, idsource, name, details, operator, operation.pce.droit_acces.etat_droit_acces, createdAt) */
    source?: {
        _id: string
        operation?: {
            pce?: {
                droit_acces?: { etat_droit_acces?: string }
            }
        }
    }
    /** Consentement valide (projection: _id, signed, signataire) */
    consentement?: unknown
    /** true si l'entreprise a accès à la source */
    hasAccess?: boolean
}

/**
 * Résultat normalisé après verifyPce (données issues de VerifyPceApiResponse).
 * dda est extrait de source.operation.pce.droit_acces.etat_droit_acces.
 */
export type VerifyPceResult = {
    source?: { _id: string }
    consentement?: unknown
    hasAccess?: boolean
    /** Statut du droit d'accès GRDF (etat_droit_acces) */
    dda: string
}

/** Résultat not found (code d'erreur API) */
export type VerifyPceNotFound = {
    notFound: true
    code: string
}

// =====================
// Service
// =====================

/**
 * Le service permettant de gérer les interactions avec GRDF
 */
export class GrdfService {
    /**
     * Vérifie si le PCE existe dans Homeys (api-metier).
     * @param params.idsource - Identifiant réel du compteur (PCE)
     * @returns VerifyPceResult si trouvé (avec dda), ou VerifyPceNotFound si not_found_source / not_found_source_with_valid_consent / not_found_source_with_valid_dda
     */
    async verifyPce(params: {
        idsource: string
    }): Promise<VerifyPceResult | VerifyPceNotFound> {
        try {
            const { data } = await axios.get<VerifyPceApiResponse>(
                "/api/metier/v5/source/search/pce",
                { params: { idsource: params.idsource } }
            )
            const dda =
                data?.source?.operation?.pce?.droit_acces?.etat_droit_acces ??
                ""
            return {
                source: data?.source ? { _id: data.source._id } : undefined,
                consentement: data?.consentement,
                hasAccess: data?.hasAccess,
                dda,
            }
        } catch (err: unknown) {
            const code =
                (
                    err as {
                        response?: { data?: { detail?: { code?: string } } }
                    }
                )?.response?.data?.detail?.code ?? "error_unknown"
            if (
                code === "not_found_source" ||
                code === "not_found_source_with_valid_consent" ||
                code === "not_found_source_with_valid_dda"
            ) {
                return { notFound: true, code }
            }
            throw err
        }
    }

    /**
     * Déclare un droit d'accès pour un consentement donné
     * @param _id_consent - Identifiant du consentement
     * @returns Le droit d'accès créé
     */
    async declare(_id_consent: string): Promise<CreateDeclarationResponse> {
        return handleServiceCall<CreateDeclarationResponse>(async () => {
            let data: CreateDeclarationResponse
            if (process.env.NODE_ENV === "development") {
                // MOCK: retourne une déclaration simulée
                data = {
                    code_postal: "75001",
                    courriel_titulaire: "titulaire@example.com",
                    date_creation: "2024-06-01T12:00:00Z",
                    date_debut_droit_acces: "2024-06-01T12:00:00Z",
                    date_fin_droit_acces: "2025-06-01T12:00:00Z",
                    date_passage_a_obsolete: null,
                    date_passage_a_refuse: null,
                    date_revocation: null,
                    etat_droit_acces: "Active",
                    id_droit_acces: "mock-droit-acces-id",
                    id_pce: "mock-pce-id",
                    numero_telephone_mobile_titulaire: null,
                    parcours: "standard",
                    perim_donnees_conso_debut: "2024-06-01",
                    perim_donnees_conso_fin: "2025-06-01",
                    perim_donnees_contractuelles: true,
                    perim_donnees_informatives: true,
                    perim_donnees_publiees: false,
                    perim_donnees_techniques: true,
                    raison_sociale_du_tiers: "Mock Tiers",
                    raison_sociale_du_titulaire: "Mock Titulaire",
                    role_tiers: "Autorisé",
                    source_passage_a_obsolete: null,
                    source_passage_a_refuse: null,
                    source_revocation: null,
                }
                // Ajoute un délai de 5 secondes pour simuler un appel réseau
                await new Promise((resolve) => setTimeout(resolve, 5000))
            } else {
                const { data: responseData } = await axios.post(
                    `/api/grdf/droit_acces`,
                    null,
                    {
                        params: {
                            _id_consentement: _id_consent,
                        },
                    }
                )
                data = responseData
            }
            return data
        })
    }

    /**
     * Récupère la dernière déclaration d'accès pour un identifiant de source donné
     * @param _id_source - Identifiant de la source
     * @returns La dernière déclaration d'accès
     */
    async getLastDeclarationByIdSource(
        _id_source: string
    ): Promise<GetLastDeclarationResponse> {
        return handleServiceCall<GetLastDeclarationResponse>(async () => {
            let data: GetLastDeclarationResponse
            if (process.env.NODE_ENV === "development") {
                // MOCK: retourne une déclaration simulée
                data = {
                    code_postal: "75001",
                    courriel_titulaire: "titulaire@example.com",
                    date_creation: "2024-06-01T12:00:00Z",
                    date_debut_droit_acces: "2024-06-01T12:00:00Z",
                    date_fin_droit_acces: "2025-06-01T12:00:00Z",
                    date_passage_a_obsolete: null,
                    date_passage_a_refuse: null,
                    date_revocation: null,
                    etat_droit_acces: "Active",
                    id_droit_acces: "mock-droit-acces-id",
                    id_pce: "mock-pce-id",
                    numero_telephone_mobile_titulaire: null,
                    parcours: "standard",
                    perim_donnees_conso_debut: "2024-06-01",
                    perim_donnees_conso_fin: "2025-06-01",
                    perim_donnees_contractuelles: true,
                    perim_donnees_informatives: true,
                    perim_donnees_publiees: false,
                    perim_donnees_techniques: true,
                    raison_sociale_du_tiers: "Mock Tiers",
                    raison_sociale_du_titulaire: "Mock Titulaire",
                    role_tiers: "Autorisé",
                    source_passage_a_obsolete: null,
                    source_passage_a_refuse: null,
                    source_revocation: null,
                }
                // Ajoute un délai de 5 secondes pour simuler un appel réseau
                await new Promise((resolve) => setTimeout(resolve, 5000))
            } else {
                const response = await axios.get(`/api/grdf/droit_acces`, {
                    params: { _id_source },
                })
                data = response.data
            }
            return data
        })
    }

    /**
     * Met à jour la date de début de récupération des données pour un consentement pour un PCE
     * @param _id_consent - id du consentement
     * @param date_from_data - nouvelle date de début de récupération des données
     */
    async updateDateFromDataByConsentId(
        _id_consent: string,
        date_from_data: string
    ) {
        return handleServiceCall<void>(async () => {
            await axios.put(`/api/metier/consentement/${_id_consent}`, {
                date_from_data,
            })
        })
    }

    /**
     * Met à jour le code postal pour un consentement pour un PCE
     * @param _id_consent - id du consentement
     * @param zipcode - nouveau code postal
     */
    async updateZipcodeByConsentId(_id_consent: string, zipcode: string) {
        return handleServiceCall<void>(async () => {
            await axios.put(`/api/metier/consentement/${_id_consent}`, {
                zipcode,
            })
        })
    }
}
